import { supabase } from '../../core/config/supabase';
import { PostEntity, PostWithDetailsEntity, PublicPostEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class PostDataSource {
  async create(
    userId: string,
    content: string,
    imageUrl?: string,
    tournamentId?: string
  ): Promise<PostEntity> {
    const { data, error } = await supabase
      .from('posts')
      .insert({
        user_id: userId,
        content,
        image_url: imageUrl,
        tournament_id: tournamentId,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as PostEntity;
  }

  async getFeed(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<PostWithDetailsEntity[]> {
    logger.debug('PostDataSource', 'Calling RPC', { functionName: 'get_feed' });

    const { data, error } = await supabase.rpc('get_feed', {
      requesting_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('PostDataSource', 'getFeed failed', { error });
      throw new Error(error.message);
    }

    // Ensure we always return an array
    if (!data || !Array.isArray(data)) {
      logger.warn('PostDataSource', 'Unexpected data format from get_feed');
      return [];
    }

    return data as PostWithDetailsEntity[];
  }

  async getPublicById(postId: string): Promise<PublicPostEntity | null> {
    const { data, error } = await supabase.rpc('get_public_post_by_id', { p_post_id: postId });
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) return null;
    return data[0] as PublicPostEntity;
  }

  async getById(postId: string): Promise<PostWithDetailsEntity | null> {
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .eq('id', postId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) return null;

    return data as PostWithDetailsEntity;
  }

  async getUserPosts(
    targetUserId: string,
    requestingUserId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<PostWithDetailsEntity[]> {
    logger.debug('PostDataSource', 'Calling RPC', { functionName: 'get_user_posts' });

    const { data, error } = await supabase.rpc('get_user_posts', {
      target_user_id: targetUserId,
      requesting_user_id: requestingUserId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('PostDataSource', 'getUserPosts failed', { error });
      throw new Error(error.message);
    }

    // Ensure we always return an array
    if (!data || !Array.isArray(data)) {
      logger.warn('PostDataSource', 'Unexpected data format from get_user_posts');
      return [];
    }

    return data as PostWithDetailsEntity[];
  }

  async update(postId: string, content: string, imageUrl?: string): Promise<PostEntity> {
    const { data, error } = await supabase
      .from('posts')
      .update({
        content,
        image_url: imageUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', postId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as PostEntity;
  }

  async delete(postId: string): Promise<void> {
    const { error } = await supabase.from('posts').delete().eq('id', postId);

    if (error) throw new Error(error.message);
  }

  async likePost(postId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('post_likes')
      .insert({
        post_id: postId,
        user_id: userId,
      });

    if (error) {
      // Ignore duplicate key error (user already liked this post)
      if (error.code !== '23505') {
        throw new Error(error.message);
      }
    }
  }

  async unlikePost(postId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('post_likes')
      .delete()
      .eq('post_id', postId)
      .eq('user_id', userId);

    if (error) throw new Error(error.message);
  }

  async getTournamentPosts(
    tournamentId: string,
    requestingUserId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<PostWithDetailsEntity[]> {
    const { data, error } = await supabase.rpc('get_tournament_posts', {
      p_tournament_id: tournamentId,
      requesting_user_id: requestingUserId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('PostDataSource', 'getTournamentPosts failed', { error });
      throw new Error(error.message);
    }

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data as PostWithDetailsEntity[];
  }
}
